import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import "./GridWithBottomArea.css";
import axios from "axios";
import CentralArea from "./CentralArea";
import { useTotemContext } from "./TotemContext";
import { useMenuContext } from "./MenuContext";
import { getVideoFromIndexedDB, saveVideoToIndexedDB } from "./db";
import { createExternalOrder } from "../backend/api";
import { motion } from 'framer-motion';
import { ReactQRCode } from "@lglab/react-qr-code";
import { ClipLoader } from "react-spinners";

import Marquee from "./Marquee";
import { VideoPlayer } from "./VideoPlayer";
import RegisterQRCode from "./RegisterQRCode";

import { useTotemAnalytics } from "../analytics/useTotemAnalytics";
import { DEFAULT_STORE_ID } from "../analytics/config";
import { useIdleTimers } from "./useIdleTimers";

// --- NUOVI COMPONENTI IMPORTATI ---
// Assicurati che questi file esistano nella stessa cartella
import PartNavigation from "./PartNavigation";
import ColorSelector from "./ColorSelector";
import DropdownSizes from "./dropdownSizes/DropdownSizes";

const IS_LOCAL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const uuid = () => (crypto.randomUUID?.() || Math.random().toString(36).slice(2));

const fixedHolidays = [
    "01-01", "06-01", "25-04", "01-05", "02-06",
    "15-08", "01-11", "08-12", "25-12", "26-12"
];

const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;

const isHoliday = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return fixedHolidays.includes(`${day}-${month}`);
};

const formatDateItalian = (date) => {
    const months = [
        "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
        "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"
    ];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

const addWorkingDays = (startDate, workDays) => {
    let date = new Date(startDate);
    let added = 0;
    while (added < workDays) {
        date.setDate(date.getDate() + 1);
        if (!isWeekend(date) && !isHoliday(date)) {
            added++;
        }
    }
    return date;
};

// --- COMPONENTE PAYMENT CARD (Invariato) ---
const PaymentCard = ({
    currentStep,
    setCurrentStep,
    cartItems,
    setCartItems,
    view,
    navigateToView,
    setHistory,
    typeMessage,
    totemId
}) => {
    const [loadingqr, setLoadingQR] = useState(true);
    const [paymentURL, setPaymentURL] = useState("");
    const [countdown, setCountdown] = useState(120);
    const [deliveryDate, setDeliveryDate] = useState(null);

    const [user, setUser] = useState({
        email: "", address: "", houseNumber: "", zipCode: "", city: "", state: "", name: "", surname: "", fullName: ""
    });

    // Calcolo della data di consegna stimata (15 giorni lavorativi)
    useEffect(() => {
        const today = new Date();
        const estimated = addWorkingDays(today, 15);
        setDeliveryDate(formatDateItalian(estimated));
    }, []);

    const handleSubmit = useCallback(() => {
        if (currentStep === 2) {
            createExternalOrder(totemId, user, cartItems, setLoadingQR, setPaymentURL);
            setCountdown(120);
            setCurrentStep(3);
        }
    }, [currentStep, totemId, user, cartItems, setCurrentStep]);

    useEffect(() => {
        if (currentStep === 2) {
            handleSubmit();
        }
    }, [currentStep, handleSubmit]);

    useEffect(() => {
        if (view === "checkout" && currentStep === 3) {
            if (countdown === 0) {
                setHistory([]);
                setCurrentStep(1);
                setCartItems([]);
                navigateToView("categories");
            }
            const interval = setInterval(() => {
                setCountdown(prev => (prev > 0 ? prev - 1 : 0));
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [countdown, view, currentStep, setHistory, setCurrentStep, setCartItems, navigateToView]);

    const formatTime = (timeInSeconds) => {
        if (timeInSeconds < 60) return `${timeInSeconds}`;
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = timeInSeconds % 60;
        return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
    };

    return (
        <div className="main-container" style={{ height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            <div className="payment-wizard" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>

                {currentStep === 1 && (
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                        {/* Area Superiore: Info e Quality Fit */}
                        <div>
                            <div style={{ textAlign: "center", padding: "10px 20px 5px 20px", marginTop: "0%" }}>
                                <i className="fas fa-cart-plus fa-4x" style={{ color: "#ccc" }}></i>
                                <h3 style={{ marginTop: "10px", color: "#999" }}>Checkout</h3>
                                <p style={{ color: "#999" }}>
                                    Hai quasi finito! Revisiona il carrello e procedi al pagamento.
                                </p>
                            </div>

                            {typeMessage === "checkout" && (
                                <div className={"sizes-alert"}>
                                    <div className="flex bg-black justify-content-center rounded-3 rounded-bottom-0 py-1">
                                        <h5 style={{ margin: 0, color: "white" }} className=" text-center blink-text-only">QUALITY FIT GUARANTEED!</h5>
                                    </div>
                                    <div className="p-3 text-center">
                                        <p style={{ fontSize: "14px" }}>Dopo l'acquisto riceverai via email le istruzioni per
                                            inviarci le tue misure corporee tramite <b>SizeYou</b>, il nostro partner
                                            tecnologico con sistema di <b>rilevazione antropometrica
                                                certificato</b>.
                                            Attraverso un semplice processo guidato,
                                            l'algoritmo rileva <b>misure corporee precise e affidabili, </b>
                                            senza l'utilizzo del metro.
                                        </p>
                                        <p style={{ fontSize: "14px" }}>Ogni prodotto viene <b>realizzato artigianalmente su
                                            ordinazione</b>. Una volta ricevuto il capo, potrai segnalarci eventuali problemi di vestibilità:
                                            effettueremo le <b>modifiche necessarie</b> senza costi aggiuntivi.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Area Inferiore: Data Spedizione Stimata (Centrata) */}
                        <div style={{
                            textAlign: 'center',
                            padding: '20px',
                            marginBottom: '10px',
                            borderTop: '1px solid #eee',
                            backgroundColor: '#fafafa',
                            borderRadius: '10px'
                        }}>
                            {deliveryDate ? (
                                <div style={{ fontSize: "15px", color: "#333" }}>
                                    <span>Se ordini oggi, consegna stimata entro il: </span>
                                    <span style={{ fontWeight: 800, color: "black", fontSize: "18px" }}>
                                        {deliveryDate}
                                    </span>
                                </div>
                            ) : (
                                <p style={{ fontSize: "14px", color: "#999" }}>Calcolo data di consegna...</p>
                            )}
                        </div>
                    </div>
                )}

                {currentStep === 3 && (
                    <div className={"qr-code-payment"} style={{ textAlign: 'center', padding: '20px' }}>
                        <h3 className="mobile-payment-text mb-5">Continua il pagamento su smartphone!</h3>
                        {loadingqr ? (
                            <ClipLoader color={"#000000"} loading={loadingqr} size={150} />
                        ) : (
                            <div>
                                <ReactQRCode
                                    value={paymentURL}
                                    size={256}
                                    marginSize={0}
                                    dataModulesSettings={{ color: "#000000", style: "rounded", randomSize: false }}
                                    finderPatternOuterSettings={{ style: "rounded" }}
                                    finderPatternInnerSettings={{ style: "rounded-sm" }}
                                    imageSettings={{
                                        src: "../../../assets/img/qr-code-logo2.png",
                                        width: 50, height: 50, excavate: true
                                    }}
                                />
                                <div className="d-flex justify-content-center align-items-center mt-4">
                                    <p className="countdowun mb-0">Questo codice scadrà tra:</p>
                                    <p className={`counttext mb-0 d-flex align-items-center ms-1 ${countdown <= 30 ? "blinkcount" : ""}`}
                                        style={{ marginTop: "2px", color: countdown <= 30 ? "red" : "black" }}>
                                        {formatTime(countdown)}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// --- COMPONENTE PRINCIPALE ---
function GridWithBottomArea() {
    const [searchParams, setSearchParams] = useSearchParams();

    // STATO NAVIGAZIONE SEQUENZIALE (Step-by-Step)
    const [selectedPartIndex, setSelectedPartIndex] = useState(0);
    const [size, setSize] = useState(null);

    const handleChangeSize = (s) => {
        setSize(s);
        if (product) product.selectedSize = s;
    }

    // Helper function (puoi metterla qui o fuori)
    const hasSizes = (product) => {
        return !product.categories.includes("Abbigliamento");
    }

    // Stato Generale
    const [activeViewIndex, setActiveViewIndex] = useState(0);
    const [loadedButtons, setLoadedButtons] = useState([]);
    const [standardSlides, setStandardSlides] = useState([]);
    const [idleSlides, setIdleSlides] = useState([]);
    const [activeContentGrid, setActiveContentGrid] = useState(null);
    const [isIdle, setIsIdle] = useState(false);
    const [showQRCode, setShowQRCode] = useState(false);
    const [activeFolder, setActiveFolder] = useState(null);
    const [lastVisitedUrls, setLastVisitedUrls] = useState({});
    const [showInfo, setShowInfo] = useState(false);
    const [videoSlug, setVideoSlug] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [view, setView] = useState("categories");
    const [slug, setSlug] = useState(() => {
        return new URLSearchParams(window.location.search).get("slug") || null;
    });
    const [history, setHistory] = useState([]);
    const [home, setHome] = useState("home-totem");
    const [product, setProduct] = useState(null);
    const [loadingProduct, setLoadingProduct] = useState(true);
    const [error, setError] = useState(null);

    // Stato Selezione
    const [selectedOptions, setSelectedOptions] = useState({});
    const [activeImages, setActiveImages] = useState([]);
    const [optionImages, setOptionImages] = useState({});

    // STATO DIPENDENZE (Logica Corretta)
    const [disabledParts, setDisabledParts] = useState({});
    const [dependencyGraph, setDependencyGraph] = useState({});
    const dependencyGraphRef = useRef({});

    // Refs
    const loadedSlugs = useRef(new Set());
    const hasInitialized = useRef(false);
    const [typeMessage, setTypeMessage] = useState("logo");
    const [showSizeWarning, setShowSizeWarning] = useState(false);

    const isProd = process.env.REACT_APP_PROD === 'true';
    const apiProdUrl = process.env.REACT_APP_API_PROD_URL;
    const apiStageUrl = process.env.REACT_APP_API_STAGE_URL_V2;
    const apiToken = process.env.REACT_APP_API_TOKEN;
    const BASE_URL = isProd ? apiProdUrl : apiStageUrl;

    const [localVideoUrls, setLocalVideoUrls] = useState({});
    const cartIdRef = useRef(uuid());
    const cartCreatedAtRef = useRef(null);
    const [showBfMarquee, setShowBfMarquee] = useState(false);

    const isRequired = (v) => v === true || v === 1 || v === '1' || v === 'true';

    // --- HELPER LOGICA ---
    const optionNameFromId = (id) => {
        const opt = product?.options?.find(o => String(o.id) === String(id));
        return opt ? opt.name : "";
    };

    const parseOption = (value) => {
        if (Array.isArray(value)) return value;
        if (typeof value === 'string') {
            if (!value.trim()) return [];
            try { return JSON.parse(value); } catch (e) { return []; }
        }
        return [];
    };

    const effectiveDisabledParts = useMemo(() => {
        if (!product || !product.options) return {};

        // Copia superficiale dello stato grezzo
        const effective = { ...disabledParts };

        Object.keys(effective).forEach(partName => {
            const blockers = effective[partName];
            const option = product.options.find(o => o.name === partName);

            if (option) {
                const eod = parseOption(option.eod);
                const totalDependencies = eod ? eod.length : 0;

                // Se ha dipendenze, controlliamo se il numero di blocchi è inferiore
                if (totalDependencies > 0) {
                    // Se chi mi blocca è meno di quanti genitori ho, vuol dire che qualcuno è attivo -> MI SBLOCCO
                    if (blockers.length < totalDependencies) {
                        delete effective[partName];
                    }
                }
            }
        });

        return effective;
    }, [disabledParts, product]);

    // --- AGGIORNAMENTO IMMAGINI ---
    const updateOptionImages = useCallback((optionName, colorValue) => {
        if (!product) return;
        const option = product.options.find((opt) => opt.name === optionName);
        if (!option) return;

        const selectedValue = option.values.find((val) => String(val.id) === String(colorValue));

        if (selectedValue) {
            // Costruisci array immagini per il viewer
            const images = [
                selectedValue.image && `https://cdn.psicopaticiservice.com/${selectedValue.image}`,
                selectedValue.image_right && `https://cdn.psicopaticiservice.com/${selectedValue.image_right}`,
                selectedValue.image_back && `https://cdn.psicopaticiservice.com/${selectedValue.image_back}`,
                selectedValue.image_left && `https://cdn.psicopaticiservice.com/${selectedValue.image_left}`,
            ].filter(Boolean);

            setOptionImages((prev) => ({ ...prev, [optionName]: images }));
        } else {
            setOptionImages((prev) => {
                const next = { ...prev };
                delete next[optionName];
                return next;
            });
        }
    }, [product]);

    // --- LOGICA DIPENDENZE RICORSIVA (CORE) ---

    // 1. handleTogglePart: Gestisce l'array disabledParts
    const handleTogglePart = (partName, hide, hasDependency, dependencyName) => {
        if (hide) {
            setOptionImages((prevImages) => {
                const updated = { ...prevImages };
                delete updated[partName];
                return updated;
            });
            // Reset selezione
            const optId = product.options.find(o => o.name === partName)?.id;
            if (optId) setSelectedOptions((prev) => ({ ...prev, [optId]: "none" }));
        }

        if (dependencyName) {
            setDisabledParts((prev) => {
                const currentDeps = prev[partName] || [];
                if (hasDependency) {
                    // Aggiungi blocco se manca
                    if (!currentDeps.includes(dependencyName)) {
                        return { ...prev, [partName]: [...currentDeps, dependencyName] };
                    }
                    return prev;
                } else {
                    // Rimuovi blocco specifico
                    const nextDeps = currentDeps.filter(d => d !== dependencyName);
                    return { ...prev, [partName]: nextDeps };
                }
            });
        }
    };

    // 2. handleDisable: Blocca ricorsivamente i figli
    const handleDisable = (partName) => {
        const option = product.options.find((opt) => opt.name === partName);
        if (!option) return;

        const graph = dependencyGraphRef.current;
        const childrenIds = Object.keys(graph).filter((key) => {
            return graph[key].includes(option.id);
        });

        if (childrenIds.length < 1) return;

        childrenIds.forEach((stringChildId) => {
            const childId = Number(stringChildId);
            const childOption = product.options.find(o => o.id === childId);
            if (!childOption) return;

            const childName = childOption.name;

            // Blocca il figlio a causa del padre disabilitato
            handleTogglePart(childName, true, true, partName);

            // Ricorsione
            handleDisable(childName);
        });
    };

    // 3. handleActivate: Sblocca ed esclude (EOA)
    const handleActivate = (partName) => {
        const option = product.options.find((opt) => opt.name === partName);
        if (!option) return;

        // EOA (Exclude On Activate) - Logica mutually exclusive
        const eoa = parseOption(option.eoa);
        if (eoa && eoa.length > 0) {
            eoa.forEach((id) => {
                const targetName = optionNameFromId(id);
                if (targetName) {
                    handleTogglePart(targetName, true, false, partName);
                    handleDisable(targetName);
                }
            });
        }

        // EOD (Exclude On Dependency) - Sblocca i figli dipendenti
        const graph = dependencyGraphRef.current;
        const childrenIds = Object.keys(graph).filter((key) => {
            return graph[key].includes(option.id);
        });

        if (childrenIds && childrenIds.length > 0) {
            childrenIds.forEach((stringChildId) => {
                const targetName = optionNameFromId(stringChildId);
                if (targetName) {
                    // Rimuove il blocco causato da questo padre
                    handleTogglePart(targetName, false, false, partName);
                }
            });
        }
    };

    // --- INIZIALIZZAZIONE PRODOTTO ---
    useEffect(() => {
        if (!product || !product.options) return;
        if (hasInitialized.current && product.slug === slug) return;

        hasInitialized.current = true;
        setSelectedPartIndex(0);
        setDisabledParts({});

        const newDependencyGraph = {};
        const initialSelections = {};
        const optionsToDisableLater = [];

        product.options.forEach((option) => {
            // Costruzione Grafo
            const rawEod = parseOption(option.eod);
            if (rawEod && Array.isArray(rawEod)) {
                const numericEod = rawEod.map(Number);
                const currentDeps = newDependencyGraph[option.id] || [];
                newDependencyGraph[option.id] = [...currentDeps, ...numericEod];
            }

            // Selezione Default
            // CORREZIONE QUI: Prendiamo SOLO se is_default è esplicitamente 1 o true.
            let def = option.values.find((v) => Number(v.is_default) === 1 || v.is_default === true);

            // ABBIAMO RIMOSSO COMPLETAMENTE IL FALLBACK "def = option.values[0]"
            // Se non c'è default, non deve selezionare nulla, nemmeno se è required.

            if (def) {
                initialSelections[option.id] = String(def.id);
            } else {
                initialSelections[option.id] = "none";
                // Se non ha un default, proviamo a disabilitarlo visivamente se ha dipendenze
                optionsToDisableLater.push(option.name);
            }
        });

        setDependencyGraph(newDependencyGraph);
        dependencyGraphRef.current = newDependencyGraph;
        setSelectedOptions(initialSelections);

        // Delay minimo per applicare visivamente stato iniziale e gestire dipendenze
        setTimeout(() => {
            if (product.image) {
                setActiveImages([`https://cdn.psicopaticiservice.com/${product.image}`]);
            }

            Object.keys(initialSelections).forEach(optId => {
                const valId = initialSelections[optId];
                if (valId !== "none") {
                    const optName = optionNameFromId(optId);
                    if (optName) updateOptionImages(optName, valId);

                    // IMPORTANTE: Se il default selezionato ha delle dipendenze (EOA), attivalo ora
                    if (optName) handleActivate(optName);
                }
            });

            // Disabilita inizialmente le parti che devono essere bloccate
            optionsToDisableLater.forEach(name => {
                handleDisable(name);
            });
        }, 50);

    }, [product, slug]);

    // --- GESTIONE SELEZIONE COLORE ---
    const handleColorSelection = (optionId, valueId) => {
        const optionName = optionNameFromId(optionId);
        if (!optionName) return;

        if (valueId === "none" || valueId === "") {
            // Disabilita figli e rimuovi selezione
            handleDisable(optionName);
            setSelectedOptions(prev => ({ ...prev, [optionId]: "none" }));
            setOptionImages((prev) => {
                const next = { ...prev };
                delete next[optionName];
                return next;
            });
            // Reset immagine principale se tocco la base
            if (product.options[0]?.id === optionId) {
                setActiveImages([`https://cdn.psicopaticiservice.com/${product.image}`]);
            }
        } else {
            // Sblocca figli e imposta selezione
            handleActivate(optionName);
            setSelectedOptions(prev => ({ ...prev, [optionId]: valueId }));
            updateOptionImages(optionName, valueId);

            // Logica Visuale (Rotazione scarpa se prevista dall'opzione)
            const option = product.options.find(o => o.id === optionId);
            if (option) {
                const visualTarget = (option.visual || "default").toLowerCase();
                if (visualTarget === "back") setActiveViewIndex(2);
                else if (visualTarget === "right") setActiveViewIndex(1);
                else if (visualTarget === "left") setActiveViewIndex(3);
                else setActiveViewIndex(0);
            }
        }
    };

    // --- NAVIGAZIONE FRECCE ---
    // --- NAVIGAZIONE FRECCE (Aggiornata con cambio vista) ---
    const handlePartChange = (direction) => {
        if (!product || !product.options) return;
        const total = product.options.length;

        // 1. Calcoliamo il nuovo indice PRIMA di aggiornare lo stato
        let nextIndex = direction === "next" ? selectedPartIndex + 1 : selectedPartIndex - 1;

        // Gestione del loop (se arrivo alla fine torno all'inizio e viceversa)
        if (nextIndex < 0) nextIndex = total - 1;
        if (nextIndex >= total) nextIndex = 0;

        // 2. Recuperiamo la "Parte" successiva
        const nextPart = product.options[nextIndex];

        // 3. CAMBIO VISTA AUTOMATICO
        // Controlliamo se la parte ha una preferenza "visual" (front, back, left, right)
        if (nextPart) {
            const visualTarget = (nextPart.visual || "default").toLowerCase();

            // Mappatura delle viste: 0=Front, 1=Right, 2=Back, 3=Left
            if (visualTarget === "back") setActiveViewIndex(2);
            else if (visualTarget === "right") setActiveViewIndex(1);
            else if (visualTarget === "left") setActiveViewIndex(3);
            else setActiveViewIndex(0); // Default o Front
        }

        // 4. Infine aggiorniamo l'indice della parte selezionata
        setSelectedPartIndex(nextIndex);
    };
    // --- EFFETTI GENERALI E ANALYTICS ---
    useEffect(() => {
        const now = new Date();
        const year = now.getFullYear();
        const start = new Date(year, 10, 7, 0, 0, 0);
        const end = new Date(year, 10, 30, 23, 59, 59);
        setShowBfMarquee(now >= start && now <= end);
    }, []);

    const { totemConfig, setTotemConfig } = useTotemContext();
    const [cartItems, setCartItems] = useState([]);
    const [homeCategory] = useState(() => {
        const fromUrl = new URLSearchParams(window.location.search).get("homeCategory");
        if (fromUrl) {
            sessionStorage.setItem("totem_homeCategory", fromUrl);
            return fromUrl;
        }
        return sessionStorage.getItem("totem_homeCategory");
    });

    const getUrlParams = () => {
        const params = new URLSearchParams(window.location.search);
        return {
            config: params.get("config"),
            totem_id: params.get("totem_id"),
            store_id: params.get("store_id"),
            view: params.get("view"),
            slug: params.get("slug"),
            home: params.get("homeCategory"),
        };
    };
    const params = getUrlParams();
    const totemId = params.totem_id || "TOTEM-LOCAL";
    const storeId = params.store_id || DEFAULT_STORE_ID;

    const analytics = useTotemAnalytics({ totem_id: totemId, store_id: storeId });

    const loadLocalVideo = async (videoPath) => {
        if (!videoPath) return;
        const src = videoPath.startsWith("http")
            ? videoPath
            : `https://api.psicopatici.com/public/${videoPath}`;

        if (IS_LOCAL) {
            setLocalVideoUrls(prev => ({ ...prev, [videoPath]: src }));
            return;
        }

        const cached = await getVideoFromIndexedDB(src);
        if (cached) {
            const objectUrl = URL.createObjectURL(cached);
            setLocalVideoUrls(prev => ({ ...prev, [videoPath]: objectUrl }));
            return;
        }
        // No cache: video element usa URL diretta (streaming). Scarica in bg per cache futura.
        try {
            const response = await fetch(src);
            if (!response.ok) throw new Error(`Failed to fetch ${src}`);
            const blob = await response.blob();
            await saveVideoToIndexedDB(src, blob);
        } catch (e) { console.error("bg cache fail", src, e); }
    };

    const idleVideoSrc = useMemo(() => {
        const file = idleSlides[0]?.file;
        if (!file) return null;
        const today = new Date().toISOString().slice(0, 10);
        return `https://api.psicopatici.com/public/${file}?date=${today}`;
    }, [idleSlides]);

    useEffect(() => {
        loadLocalVideo(idleVideoSrc);
    }, [idleVideoSrc]);

    const fetchProduct = async (productSlug) => {
        const callId = Math.random().toString(36).slice(2, 8);
        console.log(`[FETCH-PRODUCT ${callId}] ENTRY | productSlug=${JSON.stringify(productSlug)} typeof=${typeof productSlug}`);
        console.trace(`[FETCH-PRODUCT ${callId}] stack trace`);
        if (!productSlug || productSlug === "undefined" || productSlug === "null") {
            console.error(`[FETCH-PRODUCT ${callId}] ABORT - invalid slug:`, productSlug);
            return;
        }
        const api = axios.create({
            baseURL: BASE_URL,
            headers: { 'X-User-Token': apiToken },
        });

        try {
            // 1. Prima di tutto, attiviamo il loading e resettiamo TUTTI gli stati precedenti
            setLoadingProduct(true);
            setError(null);
            setProduct(null); // Rimuoviamo il vecchio prodotto

            // RESET STATI LOGICI (Importante per evitare conflitti tra prodotti diversi)
            setSelectedOptions({});
            setOptionImages({});
            setDisabledParts({});
            setDependencyGraph({});
            setActiveImages([]);
            setSelectedPartIndex(0);
            setActiveViewIndex(0);

            // Reset dei flag di controllo
            hasInitialized.current = false;

            // 2. Ora scarichiamo il nuovo prodotto
            const fullUrl = `/products/slug/${productSlug}`;
            console.log(`[FETCH-PRODUCT ${callId}] CALLING ${BASE_URL}${fullUrl}`);
            const response = await api.get(fullUrl);

            const productData = {
                ...response.data.data,
                slug: productSlug,
            };

            setProduct(productData);
            loadedSlugs.current.add(productSlug);

        } catch (err) {
            console.error("Errore nel caricamento del prodotto:", err);
            setError("Errore nel caricamento del prodotto.");
        } finally {
            setLoadingProduct(false);
        }
    };

    useEffect(() => {
        setView(searchParams.get("view") || "categories");
    }, [searchParams]);

    const navigateToView = (newView, newSlug = null, history = true) => {
        const navId = Math.random().toString(36).slice(2, 8);
        console.log(`[NAV ${navId}] navigateToView("${newView}", ${JSON.stringify(newSlug)}, history=${history}) | typeof newSlug=${typeof newSlug} | current slug=${JSON.stringify(slug)}, homeCategory=${JSON.stringify(homeCategory)}`);
        console.trace(`[NAV ${navId}] stack trace`);

        if (history)
            setHistory((prev) => [
                ...prev,
                { view: view, slug: slug },
            ]);

        setView(newView);
        setSlug(newSlug);
        setSearchParams((prev) => {
            prev.set("view", newView);
            if (newSlug) {
                prev.set("slug", newSlug);
            } else {
                prev.delete("slug");
            }
            return prev;
        });

        if (newView === "product" && newSlug) {
            console.log(`[NAV ${navId}] → calling fetchProduct(${JSON.stringify(newSlug)})`);
            analytics.trackProductClick(String(newSlug));
            setLoadingProduct(true);
            hasInitialized.current = false;
            fetchProduct(newSlug);
        } else if (newView === "product") {
            console.warn(`[NAV ${navId}] product view ma SENZA slug! newSlug=${JSON.stringify(newSlug)}`);
        }
        if (newView === "categories" && newSlug) {
            analytics.trackCategoryView(String(newSlug));
        }
    };

    const currentTotalPrice = useMemo(() => {
        if (!product) return 0;

        // Partiamo dal prezzo base (sale_price se in offerta, altrimenti price)
        let total = product.sale_price ? parseFloat(product.sale_price) : parseFloat(product.price);

        // Sommiamo i sovrapprezzi delle opzioni selezionate
        if (product.options && selectedOptions) {
            product.options.forEach(opt => {
                const selectedId = selectedOptions[opt.id];
                if (selectedId && selectedId !== "none") {
                    const val = opt.values.find(v => String(v.id) === String(selectedId));
                    if (val && val.price_adjustment) {
                        total += parseFloat(val.price_adjustment);
                    }
                }
            });
        }
        return total;
    }, [product, selectedOptions]);


    const wrapProduct = () => {
        const getCleanPath = (path) => {
            if (!path || path === "null" || path === "undefined") return null;
            return path.replace("https://cdn.psicopaticiservice.com/", "").replace("http://cdn.psicopaticiservice.com/", "");
        };

        // Pulisce i nomi tecnici (es: "Busto - UD333" -> "Busto")
        const cleanTechnicalName = (name) => {
            if (!name) return "";
            return name.split('-')[0].trim();
        };

        const firstOpt = product.options?.[0];
        let baseValueName = "Configurazione";
        let mainCartImage = product.image;

        if (firstOpt) {
            const firstSelectedId = selectedOptions[firstOpt.id];
            const val = firstOpt.values?.find(v => String(v.id) === String(firstSelectedId)) ||
                firstOpt.values?.find(v => v.is_default || v.required);

            if (val) {
                baseValueName = val.label || val.name;
                if (val.image) mainCartImage = val.image;
            }
        }

        const cartOptions = {};
        const optionInfo = {}; // QUI SALVEREMO I NOMI DELLE OPZIONI (es: {2161: "Cappuccio"})

        if (product.options) {
            product.options.forEach((opt, index) => {
                const selectedId = selectedOptions[opt.id];
                if (selectedId === "none") return;

                let val = null;
                if (selectedId) {
                    val = opt.values.find((v) => String(v.id) === String(selectedId));
                } else {
                    val = opt.values.find(v => v.is_default == 1 || v.is_default === true);
                    if (!val && (opt.required == 1 || opt.required === true) && opt.values.length > 0) {
                        val = opt.values[0];
                    }
                }

                if (val) {
                    const displayName = cleanTechnicalName(opt.set_name || opt.name);
                    const displayLabel = val.label || val.name;

                    const optionData = {
                        affect_price: val.price_adjustment ? parseFloat(val.price_adjustment) : 0,
                        option_id: opt.id,
                        value_id: val.id,
                        option_name: displayName,
                        option_value: displayLabel, // VALORE (es: Nero)
                        image: getCleanPath(val.image),
                        order: index
                    };

                    if (!cartOptions[opt.id]) cartOptions[opt.id] = [];
                    cartOptions[opt.id].push(optionData);

                    // SALVIAMO IL NOME NELL'INFO PER IL CARRELLO
                    optionInfo[opt.id] = displayName;
                }
            });
        }

        return {
            // ... (tutto il resto dell'oggetto rimane uguale)
            id: product.id,
            cart_item_id: uuid(),
            qty: 1,
            is_sizeable: product.is_sizeable,
            name: product.name,
            price: parseFloat(product.price),
            sale_price: product.sale_price ? parseFloat(product.sale_price) : null,
            image: getCleanPath(mainCartImage),
            abbigliamento: product.categories && product.categories.includes("Abbigliamento"),
            selectedSize: product.selectedSize || null,
            options: {
                base: {
                    option_id: firstOpt?.id ?? null,
                    name: cleanTechnicalName(firstOpt?.name) || "Base",
                    value: baseValueName,
                },
                image: getCleanPath(mainCartImage),
                options: {
                    optionInfo: optionInfo,
                    optionCartValue: cartOptions
                }
            },
        };
    };

    const handleCartButton = () => {
        setTimeout(() => {
            if (view === "product") {
                if (product.attributes.find(attr => attr.set_name === "Taglia" || attr.set_name === "Taglie") && !product.selectedSize) {
                    setShowSizeWarning(true);
                    setTimeout(() => setShowSizeWarning(false), 3000);
                    return;
                }
                if (cartItems.length === 0) {
                    cartIdRef.current = uuid();
                    cartCreatedAtRef.current = Date.now();
                    analytics.trackCart("cart_created", { cart_id: cartIdRef.current });
                }
                setCartItems([...cartItems, wrapProduct()])
                handleBack();
            } else if (cartItems.length !== 0) {
                if (view !== "checkout") {
                    navigateToView("checkout");
                } else {
                    setCurrentStep(prev => Math.min(prev + 1, 3));
                }
            }
        }, 150);
    };

    const handleHome = () => {
        setTimeout(() => {
            setSlug(null);
            setActiveFolder(null);
            setCurrentStep(1);
            navigateToView("categories", null)
        }, 150);
    };

    const handleBack = () => {
        if (view === "checkout") {
            if (currentStep === 1) {
                const lastView = history[history.length - 1];
                // Se torniamo indietro dal checkout e non c'è slug, passiamo null (che verrà gestito come home-totem)
                navigateToView("categories", lastView?.slug || null, false);
            }
            if (currentStep === 3) {
                setCurrentStep(1);
            }
        } else if (history.length > 0) {
            const lastView = history[history.length - 1];
            setHistory((prev) => prev.slice(0, -1));
            setView(lastView.view);

            // CORREZIONE QUI:
            // Puliamo lo slug se è la stringa "null" o undefined
            const restoredSlug = (lastView.slug && lastView.slug !== "null") ? lastView.slug : null;

            setSlug(restoredSlug);

            setSearchParams((prev) => {
                prev.set("view", lastView.view);

                // Se c'è uno slug valido lo impostiamo, altrimenti LO RIMUOVIAMO (così non diventa "null" stringa)
                if (restoredSlug) {
                    prev.set("slug", restoredSlug);
                } else {
                    prev.delete("slug");
                }
                return prev;
            });
        } else if (view === "categories") {
            setActiveFolder(null);
        } else {
            console.log("Nessuna vista precedente disponibile");
        }
    };

    useIdleTimers({
        cartItems,
        cartCreatedAtRef,
        cartIdRef,
        analytics,
        navigateToView,
        setIsIdle,
        setHistory,
        setCurrentStep,
        setCartItems
    });

    useEffect(() => {
        const paramsNow = getUrlParams();
        if (paramsNow.view) {
            setView(paramsNow.view);
        }
        if (paramsNow.slug) {
            setSlug(paramsNow.slug);
        }
        if (paramsNow.view === "categories") {
            localStorage.setItem('currentImageIndex', 0)
            if (paramsNow.slug === "scarpe-e-borse" || paramsNow.slug === "scarpe-uomo" || paramsNow.slug === "scarpe-donna") {
                setTypeMessage("logo");
            } else if (paramsNow.slug === "borse-donna") {
                setTypeMessage("logo");
            } else {
                setTypeMessage("abbigliamento");
            }
        } else if (paramsNow.view == "checkout") {
            const hasSizeableProduct = cartItems.some(item =>
                item.is_sizeable === 1 || item.is_sizeable === true || item.is_sizeable === "1"
            );

            if (hasSizeableProduct) {
                setTypeMessage("checkout");
            } else {
                setTypeMessage("logo");
            }
        }

        if ((paramsNow.view == "categories" && !paramsNow.slug) || paramsNow.slug == "null") {
            setTypeMessage("logo");
        }
    }, [searchParams, cartItems]);

    // Totem Slides Fetch
    useEffect(() => {
        const totemsParams = searchParams.get("totem_id");
        if (!totemsParams) return;

        fetch(`https://api.psicopatici.com/api/totems/${totemsParams}/slides`)
            .then(r => r.json())
            .then(data => setStandardSlides(Array.isArray(data) ? data : []))
            .catch(err => console.error("slides fetch error:", err));

        fetch(`https://api.psicopatici.com/api/totems/${totemsParams}/video-idle`)
            .then(r => r.json())
            .then(data => setIdleSlides(Array.isArray(data) ? data : []))
            .catch(err => console.error("video-idle fetch error:", err));
    }, []);

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.origin === "https://store.psicopatici.com") {
                const { lastVisited } = event.data;
                if (lastVisited && activeContentGrid?.element_id) {
                    setLastVisitedUrls((prev) => ({
                        ...prev,
                        [activeContentGrid.element_id]: lastVisited,
                    }));
                }
            }
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [activeContentGrid]);

    const buttonClickHandler = (button) => {
        if (button.type === "placeholder") return;
        if (button.id === 8) {
            setShowQRCode(true);
            return;
        }
        setActiveContentGrid(button);
    };

    const closeQRCode = () => {
        setShowQRCode(false);
    };

    // Ref e handler per il video idle
    const idleVideoRef = useRef(null);
    const audioUnlockedRef = useRef(false);

    // Sblocca l'audio al primo click/touch dell'utente
    useEffect(() => {
        const unlockAudio = () => {
            if (audioUnlockedRef.current) return;

            // Crea un elemento audio silenzioso per sbloccare l'autoplay
            const silentAudio = new Audio();
            silentAudio.src = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=";
            silentAudio.play().then(() => {
                audioUnlockedRef.current = true;
                console.log("Audio autoplay unlocked");
            }).catch(() => { });

            // Rimuovi listener dopo il primo unlock
            window.removeEventListener("click", unlockAudio);
            window.removeEventListener("touchstart", unlockAudio);
        };

        window.addEventListener("click", unlockAudio);
        window.addEventListener("touchstart", unlockAudio);

        return () => {
            window.removeEventListener("click", unlockAudio);
            window.removeEventListener("touchstart", unlockAudio);
        };
    }, []);

    // Effetto per gestire il play del video quando entra in idle
    useEffect(() => {
        if (isIdle && idleVideoRef.current) {
            const video = idleVideoRef.current;

            // Forza play
            video.play().then(() => {
                console.log("Idle video playing successfully");
            }).catch((err) => {
                console.warn("Idle video play blocked:", err.message);
                // Fallback: prova con muted poi riabilita audio
                video.muted = true;
                video.play().then(() => {
                    // Dopo un breve delay, riabilita l'audio
                    setTimeout(() => {
                        video.muted = false;
                    }, 100);
                }).catch(() => { });
            });
        }
    }, [isIdle]);

    const handleIdleVideoError = useCallback(() => {
        const video = idleVideoRef.current;
        if (video) {
            console.warn("Idle video error, attempting reload...");
            video.load();
            video.play().catch(() => { });
        }
    }, []);

    const handleIdleVideoStalled = useCallback(() => {
        const video = idleVideoRef.current;
        if (video && video.paused) {
            console.warn("Idle video stalled, attempting to resume...");
            video.play().catch(() => { });
        }
    }, []);

    const handleCanPlay = useCallback(() => {
        const video = idleVideoRef.current;
        if (video) {
            video.play().catch((err) => {
                console.warn("Autoplay fallito, riprovo mutato:", err);
                video.muted = true;
                video.play();
            });
        }
    }, []);

    if (isIdle && idleVideoSrc) {
        return (
            <div className="carousel-fullscreen" style={{ backgroundColor: 'black' }}>
                <video
                    ref={idleVideoRef}
                    src={localVideoUrls[idleVideoSrc] || idleVideoSrc}
                    loop
                    playsInline
                    muted // Parti SEMPRE mutato a livello di attributo per massima compatibilità
                    onCanPlay={handleCanPlay} // Gestisci il play qui
                    onError={handleIdleVideoError}
                    onStalled={handleIdleVideoStalled}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block" // Evita spazi vuoti inline
                    }}
                />
            </div>
        );
    }
    // --- NON PIÙ UN COMPONENTE, MA UNA FUNZIONE DI RENDER ---
    const renderColorEditor = () => {
        // NOTA: Ho rimosso useState e handleChangeSize da qui perché sono stati spostati sopra

        // Recuperiamo la parte corrente
        const currentPart = product?.options ? product.options[selectedPartIndex] : null;
        const baseSetName = product?.options?.find(o => o.name === "Base")?.name;

        if (loadingProduct) return <div>Caricamento...</div>;
        if (error) return <div>{error}</div>;
        if (!product) return <div>Prodotto non trovato.</div>;

        return (
            <div className="main-container" style={{ height: "100%", display: "flex", flexDirection: "column" }}>

                {/* 1. Selezione Taglia (se presente) */}
                {hasSizes(product) &&
                    <div style={{ flexShrink: 0, marginTop: "10px" }}>
                        <DropdownSizes product={product} handleChangeSize={handleChangeSize} current={size} />
                    </div>
                }

                {/* 2. Barra Info Prodotto */}
                <div style={{ flexShrink: 0 }} className="product-info-bar d-flex align-items-center justify-content-between px-3">
                    <div className={`product-name d-flex align-items-center`}>
                        {product.name}
                        <div
                            className="info-icon ms-2"
                            onClick={() => setShowInfo(!showInfo)}
                            title="Clicca per maggiori informazioni"
                        >
                            <i className="fas fa-info"></i>
                        </div>
                    </div>
                    <div className="product-price-dynamic fw-bold" style={{ fontSize: '1.2rem', color: '#000' }}>
                        {currentTotalPrice.toFixed(2)}€
                    </div>
                </div>

                {showInfo && (
                    <div style={{ zIndex: "10000" }} className={`product-tooltip ${hasSizes(product) ? "sizeable" : "asda"}`}
                        dangerouslySetInnerHTML={{ __html: product.description }}>
                    </div>
                )}

                {/* 3. CONTENUTO CENTRALE: Navigazione + Selettore Colore */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#fff', overflow: 'hidden' }}>

                    {/* A. Navigazione a Frecce */}
                    {currentPart && (
                        <div style={{ borderBottom: '1px solid #eee', flexShrink: 0 }}>
                            <PartNavigation
                                selectedPart={currentPart}
                                selectedPartIndex={selectedPartIndex}
                                totalParts={product.options.length}
                                handlePartChange={handlePartChange}
                                baseSetName={baseSetName}
                            />
                        </div>
                    )}

                    {/* B. Griglia Colori Nuova */}
                    {/* IMPORTANTE: L'overflow è qui. Ora che questa è una funzione render, React non distrugge questo div */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '10px', paddingBottom: '20px' }}>
                        {currentPart ? (
                            <ColorSelector
                                selectedPart={currentPart}
                                selectedColor={selectedOptions[currentPart.id]}
                                handleColorSelect={handleColorSelection}
                                disabledParts={effectiveDisabledParts}
                                allOptions={product?.options || []}
                            />
                        ) : (
                            <div className="d-flex justify-content-center align-items-center h-100">
                                Caricamento opzioni...
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const NavigationBar = ({ setView, setSlug, setSearchParams, view, slug }) => {
        const { menuItems } = useMenuContext();
        const [activeIconIndex, setActiveIconIndex] = useState(null);

        useEffect(() => {
            const params = new URLSearchParams(window.location.search);
            const updatedView = params.get("view") || "categories";
            const updatedSlug = params.get("slug") || null;

            if (view !== updatedView || slug !== updatedSlug) {
                setActiveIconIndex(null);
            }
        }, [window.location.search]);

        const handleIconClick = (index, newView, newSlug) => {
            const viewToSet = newSlug && newSlug.includes("https://") ? "iframe" : newView;
            if (viewToSet === "categories" && newSlug) {
                analytics.trackCategoryView(String(newSlug));
            }
            if (index !== activeIconIndex) {
                setActiveIconIndex(index);
            }
            navigateToView(viewToSet, newSlug);
        };

        const handleFolderClick = (folder, newView) => {
            const viewToSet = folder.url && folder.url.includes("https://") ? "iframe" : newView;
            if (viewToSet === "categories" && folder.url) {
                analytics.trackCategoryView(String(folder.url));
            }
            navigateToView(viewToSet, folder.url);
            setActiveFolder(folder);
        };

        const generateRandomColor = () => {
            const letters = "0123456789ABCDEF";
            let color = "#";
            for (let i = 0; i < 6; i++) {
                color += letters[Math.floor(Math.random() * 16)];
            }
            return color;
        };

        if (view !== "categories" && view !== "iframe") {
            return null;
        }

        return (
            <div className="menu-grid">
                {activeFolder && (
                    <div className="overlay" style={{
                        position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1,
                    }}
                    ></div>
                )}
                {!activeFolder && (
                    <>
                        <div className="d-flex px-4 justify-content-between w-100">
                            {menuItems.map((item, index) => (
                                <div className="menu-wrapper" key={item.id}>
                                    <motion.div
                                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
                                        className={`menu-item ${index === activeIconIndex ? "active" : ""}`}
                                        onClick={() => {
                                            item.children.length > 0 ? handleFolderClick(item, "categories") : handleIconClick(index, "categories", item.url);
                                            localStorage.setItem("updateSlug", item.url);
                                        }}
                                        style={{
                                            backgroundImage: item.icon ? `url(https://cdn.psicopaticiservice.com/${item.icon})` : "none",
                                            backgroundColor: !item.icon ? generateRandomColor() : "transparent",
                                            backgroundSize: "cover", backgroundPosition: "center",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            color: "#fff", fontWeight: "bold", textShadow: "0 1px 2px rgba(0, 0, 0, 0.5)",
                                            height: "100px", width: "100px", cursor: "pointer", boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                                        }}
                                    />
                                    <span className="menu-title">{item.title}</span>
                                </div>
                            ))}
                        </div>
                        <RegisterQRCode totemId={totemId} />
                    </>
                )}
                {activeFolder && (
                    <div className="folder-view" style={{
                        position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
                        zIndex: 2, display: "flex", flexDirection: "column", padding: "10px", // <-- Cambiato in "column"
                    }}
                    >
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5 }}
                            className="d-flex flex-column w-100 h-100" // <-- Tolto reverse, aggiunto h-100
                        >

                            {/* 1. GRIGLIA CATEGORIE IN ALTO */}
                            <div className="folder-grid mt-4">
                                {activeFolder.children.map((child, index) => (
                                    <div className="folder-wrapper" key={child.id}>
                                        <motion.div
                                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
                                            className={`folder-item ${index === activeIconIndex ? "active" : ""}`}
                                            style={{
                                                backgroundImage: child.icon ? `url(https://cdn.psicopaticiservice.com/${child.icon})` : "none",
                                                backgroundColor: !child.icon ? generateRandomColor() : "transparent",
                                                backgroundSize: "cover", backgroundPosition: "center",
                                                height: "100px", width: "100px", display: "flex", alignItems: "center", justifyContent: "center",
                                                color: "#fff", fontWeight: "bold", textShadow: "0 1px 2px rgba(0, 0, 0, 0.5)", cursor: "pointer",
                                            }}
                                            onClick={() => {
                                                if (child.children && child.children.length > 0) {
                                                    handleFolderClick(child);
                                                } else {
                                                    handleIconClick(index, "categories", child.url);
                                                    localStorage.setItem("updateSlug", child.url);
                                                }
                                            }}
                                        >
                                        </motion.div>
                                        <span style={{ fontWeight: "400" }} className="folder-title">{child.title}</span>
                                    </div>
                                ))}
                            </div>

                            {/* 2. TITOLO E SEPARATORE SPINTI IN BASSO CON MT-AUTO */}
                            <div className="mt-auto mb-3">
                                <div className="m-0 mb-4" style={{ paddingLeft: "32%", paddingRight: "32%" }}>
                                    <hr className="m-0" style={{ border: '3px solid black', opacity: 1, borderRadius: "10px" }} />
                                </div>
                                <h3 className="folder-title mt-0" style={{ textAlign: "center", marginBottom: "0px", fontSize: "18px", fontWeight: "bold", lineHeight: "1.4" }}>
                                    {activeFolder.title}
                                </h3>
                            </div>

                        </motion.div>
                    </div>
                )}
            </div>
        );
    };

    const showButtonName = (view) => {
        switch (view) {
            case "checkout": return "PROCEDI";
            case "product": return "AGGIUNGI AL CARRELLO";
            default: return `${cartItems.length} CASSA`;
        }
    }

    // MAIN RENDER
    return (
        <div className="grid-with-central-area">
            {showQRCode && activeContentGrid?.type === "url" && (
                <div className="qr-code-overlay" onClick={closeQRCode}>
                    <ReactQRCode value={activeContentGrid.content} size={256} />
                    <p>Scansiona per continuare la navigazione sul tuo smartphone</p>
                </div>
            )}

            <div className="grid-row top-row" style={{ position: "relative" }}>

                <div style={{
                    position: "absolute",
                    bottom: "0px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                    color: "#ffffff",
                    padding: "15px 100px 10px 100px",
                    borderRadius: "30px",
                    borderBottomLeftRadius: "0px",
                    borderBottomRightRadius: "0px",
                    zIndex: 100, /* Assicura che stia sopra al video */
                    fontSize: "18px",
                    fontWeight: "bold",
                    letterSpacing: "1px",
                    pointerEvents: "none" /* Fa sì che non blocchi eventuali click sul video sotto */
                }}>
                    <img src="/PSIv1white.png" alt="" style={{ width: "150px" }} />
                    <p className="mb-0">WWW.PSICOPATICI.COM</p>
                </div>

                {standardSlides.length > 0 ? (
                    <VideoPlayer button={{ content: standardSlides.map(s => ({ url: s.file })) }} />
                ) : (
                    <div className="banner hide-placeholder" />
                )}
            </div>

            <div className="grid-middle-row">
                <CentralArea
                    selectedOptions={selectedOptions}
                    product={product}
                    activeImages={activeImages}
                    optionImages={optionImages}
                    cartItems={cartItems}
                    setCartItems={setCartItems}
                    view={view}
                    slug={(() => {
                        const cleanSlug = (slug && slug !== "null" && slug !== "undefined") ? slug : null;
                        const cleanHomeCategory = (homeCategory && homeCategory !== "null" && homeCategory !== "undefined") ? homeCategory : null;
                        const effectiveSlug = cleanSlug || cleanHomeCategory;
                        if (view === "categories" && effectiveSlug !== cleanHomeCategory) {
                            console.log(`[RENDER] CentralArea slug=${JSON.stringify(effectiveSlug)} (slug=${JSON.stringify(slug)}, homeCategory=${JSON.stringify(homeCategory)})`);
                        }
                        if (view === "categories" && !effectiveSlug) {
                            console.warn(`[BUG?] CentralArea slug è null! slug=${JSON.stringify(slug)}, homeCategory=${JSON.stringify(homeCategory)}, URL=${window.location.search}`);
                        }
                        return effectiveSlug;
                    })()}
                    navigateToView={navigateToView}
                    step={currentStep}
                    activeViewIndex={activeViewIndex}
                    setActiveViewIndex={setActiveViewIndex}
                    disabledParts={effectiveDisabledParts}
                    totemId={totemId}
                />

                <div className="side-column-v2" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>

                    <div className="adv-marquee" style={{ flexShrink: 0 }}>
                        {showBfMarquee ? (
                            <Marquee text="10% AL PRIMO ACQUISTO DOPO LA REGISTRAZIONE &nbsp; &nbsp; &nbsp;" />
                        ) : (
                            <>
                                {/* Messaggio Scarpe (Corto -> Veloce) */}
                                {typeMessage === "scarpe" && (
                                    <Marquee text="CONCLUDI L'ACQUISTO E RICEVERAI VIA EMAIL LE INDICAZIONI PER COMUNICARCI LA TUA MISURA. &nbsp; &nbsp; &nbsp;" />
                                )}

                                {/* Messaggio Abbigliamento (Corto -> Veloce) */}
                                {typeMessage === "abbigliamento" && (
                                    <Marquee text="CONCLUDI L'ACQUISTO E RICEVERAI VIA EMAIL LE INDICAZIONI PER COMUNICARCI LE TUE MISURE. &nbsp; &nbsp; &nbsp;" />
                                )}

                                {/* Messaggio Checkout (Lungo -> Lento automatico) */}
                                {typeMessage === "checkout" && (
                                    <Marquee text="DOPO L’ACQUISTO RICEVERAI MEZZO MAIL LE ISTRUZIONI PER UTILIZZARE L'APP E COMUNICARCI LE TUE MISURE. " />
                                )}

                                {/* Messaggio Logo (Molto Lungo -> Lento automatico) */}
                                {typeMessage === "logo" && (
                                    <Marquee text="PERSONALIZZA IL TUO PRODOTTO &nbsp; &nbsp; &nbsp; PRODUZIONE SU ORDINAZIONE &nbsp; &nbsp; &nbsp; LABORATORIO ARTIGIANALE 100% MADE IN ITALY &nbsp; &nbsp;&nbsp;&nbsp; 10% DI SCONTO AL PRIMO ACQUISTO DOPO LA REGISTRAZIONE &nbsp; &nbsp; &nbsp;" />
                                )}
                            </>
                        )}
                    </div>

                    <NavigationBar view={view} />

                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                        {view === "product" && (!loadingProduct && renderColorEditor())}

                        {view === "checkout" &&
                            <PaymentCard
                                currentStep={currentStep}
                                setCurrentStep={setCurrentStep}
                                cartItems={cartItems}
                                setCartItems={setCartItems}
                                view={view}
                                navigateToView={navigateToView}
                                setHistory={setHistory}
                                typeMessage={typeMessage}
                                totemId={totemId}
                            />
                        }

                        {view === "iframe" && (<div className="main-container"></div>)}
                    </div>

                    <div style={{ flexShrink: 0 }} className="">
                        {currentStep < 2 &&
                            <motion.div style={{ zIndex: "9999" }} className="header-bar">
                                <div className="flex-1">
                                    {view !== "categories" && (
                                        <button style={{ zIndex: "99999" }} hidden={currentStep >= 2}
                                            className="btn btn-black me-0"
                                            onClick={() => handleCartButton()}>
                                            {showButtonName(view)}
                                        </button>
                                    )}
                                    {view === "categories" && (
                                        <button className="btn1 me-0" onClick={() => handleCartButton()}>
                                            CASSA
                                            {cartItems.length > 0 && <span className="badge">{cartItems.length}</span>}
                                        </button>
                                    )}
                                </div>
                                <div className="flex-center">
                                    <button className="btn1" onClick={() => handleHome()}>
                                        <i className="fa-solid fa-house fa-2xl" style={{ color: "#ffffff" }}></i>
                                    </button>
                                </div>
                                <div className="flex-1">
                                    <button className="btn btn-white ms-0 me-0" onClick={handleBack}>
                                        <i className="fas fa-chevron-left"></i> INDIETRO
                                    </button>
                                </div>
                            </motion.div>
                        }
                        {currentStep >= 2 &&
                            <div className="header-bar">
                                <motion.button
                                    initial={{ opacity: 0.2 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1 }}
                                    className="btn btn-white" onClick={handleBack}>
                                    <i className="fas fa-chevron-left"></i> INDIETRO
                                </motion.button>
                            </div>
                        }
                    </div>
                </div>
            </div>

            {/* Popup avviso taglia mancante */}
            {showSizeWarning && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}
                    onClick={() => setShowSizeWarning(false)}
                >
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: '16px',
                            padding: '30px 40px',
                            textAlign: 'center',
                            maxWidth: '400px',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <i className="fas fa-ruler fa-3x" style={{ color: '#ff9800', marginBottom: '15px' }}></i>
                        <h4 style={{ margin: '10px 0', fontWeight: 700 }}>Seleziona la taglia</h4>
                        <p style={{ color: '#666', fontSize: '15px', margin: 0 }}>
                            Per aggiungere il prodotto al carrello devi prima selezionare una taglia.
                        </p>
                        <button
                            onClick={() => setShowSizeWarning(false)}
                            style={{
                                marginTop: '20px',
                                width: '100%',
                                padding: '12px 30px',
                                border: 'none',
                                borderRadius: '8px',
                                backgroundColor: '#000',
                                color: '#fff',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: 'pointer'
                            }}
                        >
                            OK, capito
                        </button>
                    </motion.div>
                </div>
            )}
        </div>
    );
}

export default GridWithBottomArea;